import React, { useState } from "react";
import {
  Download,
  BookOpen,
  FileText,
  Play,
  Image,
  Music,
  Search,
  Filter,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";
import "./StudentDownloads.css";

const StudentDownloads = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("recent");

  // Mock downloaded content
  const downloadedContent = [
    {
      id: 1,
      title: "Advanced Mathematics Textbook",
      author: "Dr. Sarah Johnson",
      type: "ebook",
      category: "Mathematics",
      downloadDate: "2024-01-15",
      fileSize: "15.2 MB",
      format: "PDF",
      description: "Comprehensive guide to advanced mathematical concepts",
      thumbnail: "📚",
      isDownloaded: true,
    },
    {
      id: 2,
      title: "Physics Lab Experiments",
      author: "Prof. Michael Chen",
      type: "video",
      category: "Science",
      downloadDate: "2024-01-12",
      fileSize: "245.8 MB",
      format: "MP4",
      description: "Video demonstrations of physics experiments",
      thumbnail: "🎥",
      isDownloaded: true,
    },
    {
      id: 3,
      title: "World History Timeline",
      author: "Dr. Emily Davis",
      type: "interactive",
      category: "History",
      downloadDate: "2024-01-10",
      fileSize: "8.7 MB",
      format: "HTML",
      description: "Interactive timeline of world history",
      thumbnail: "🗺️",
      isDownloaded: true,
    },
    {
      id: 4,
      title: "English Literature Audio",
      author: "Prof. James Wilson",
      type: "audio",
      category: "Literature",
      downloadDate: "2024-01-08",
      fileSize: "67.3 MB",
      format: "MP3",
      description: "Audio readings of classic literature",
      thumbnail: "🎵",
      isDownloaded: true,
    },
    {
      id: 5,
      title: "Biology Diagrams",
      author: "Dr. Lisa Brown",
      type: "image",
      category: "Science",
      downloadDate: "2024-01-05",
      fileSize: "12.4 MB",
      format: "ZIP",
      description: "High-quality biology diagrams and illustrations",
      thumbnail: "🖼️",
      isDownloaded: true,
    },
  ];

  const categories = ["All", "Mathematics", "Science", "History", "Literature"];
  const sortOptions = [
    { value: "recent", label: "Most Recent" },
    { value: "title", label: "Title A-Z" },
    { value: "size", label: "File Size" },
    { value: "category", label: "Category" },
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case "ebook":
        return <BookOpen size={20} className="type-icon ebook" />;
      case "video":
        return <Play size={20} className="type-icon video" />;
      case "audio":
        return <Music size={20} className="type-icon audio" />;
      case "image":
        return <Image size={20} className="type-icon image" />;
      case "interactive":
        return <FileText size={20} className="type-icon interactive" />;
      default:
        return <FileText size={20} className="type-icon" />;
    }
  };

  const filteredContent = downloadedContent
    .filter((item) => {
      const matchesSearch =
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "recent":
          return new Date(b.downloadDate) - new Date(a.downloadDate);
        case "title":
          return a.title.localeCompare(b.title);
        case "size":
          return parseFloat(b.fileSize) - parseFloat(a.fileSize);
        case "category":
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

  const handleDownload = (item) => {
    toast.success(`Downloading ${item.title}...`);
    // Simulate download
    setTimeout(() => {
      toast.success(`${item.title} downloaded successfully!`);
    }, 2000);
  };

  const handleDelete = (itemId) => {
    toast.success("Content removed from downloads");
    // In a real app, this would remove from the downloads list
  };

  const getTotalSize = () => {
    return downloadedContent
      .reduce((total, item) => {
        return total + parseFloat(item.fileSize);
      }, 0)
      .toFixed(1);
  };

  return (
    <div className="student-downloads">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <Download size={24} />
          </div>
          <div>
            <h1>My Downloads</h1>
            <p>Access your downloaded e-library content</p>
          </div>
        </div>
        <div className="download-stats">
          <div className="stat-card">
            <span className="stat-number">{downloadedContent.length}</span>
            <span className="stat-label">Items Downloaded</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{getTotalSize()} MB</span>
            <span className="stat-label">Total Size</span>
          </div>
        </div>
      </div>

      <div className="downloads-content">
        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search downloaded content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filters-row">
            <div className="filter-group">
              <Filter size={16} />
              <label>Category:</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Downloads Grid */}
        <div className="downloads-grid">
          {filteredContent.length === 0 ? (
            <div className="empty-state">
              <Download size={48} className="empty-icon" />
              <h3>No downloads found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            filteredContent.map((item) => (
              <div key={item.id} className="download-card">
                <div className="card-header">
                  <div className="content-thumbnail">{item.thumbnail}</div>
                  <div className="content-type">{getTypeIcon(item.type)}</div>
                </div>

                <div className="card-body">
                  <h3 className="content-title">{item.title}</h3>
                  <p className="content-author">by {item.author}</p>
                  <p className="content-description">{item.description}</p>

                  <div className="content-meta">
                    <span className="content-category">{item.category}</span>
                    <span className="content-format">{item.format}</span>
                  </div>

                  <div className="content-details">
                    <div className="detail-item">
                      <span className="detail-label">Downloaded:</span>
                      <span className="detail-value">{item.downloadDate}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Size:</span>
                      <span className="detail-value">{item.fileSize}</span>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <button
                    className="action-btn primary"
                    onClick={() => handleDownload(item)}
                  >
                    <Download size={16} />
                    Download Again
                  </button>
                  <button
                    className="action-btn secondary"
                    onClick={() => handleDelete(item.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDownloads;
